import { capture } from './analytics';

type DonationEvent =
  // Happy path
  | 'donation_modal_opened'
  | 'donation_amount_selected'
  | 'donation_details_submitted'
  | 'donation_checkout_initiated'
  | 'donation_payment_completed'
  | 'donation_completed'
  // UX / funnel
  | 'donation_step_viewed'
  | 'donation_modal_closed'
  | 'donation_validation_shown'
  // Hiccups
  | 'donation_redirect_resumed'
  // Session recovery
  | 'donation_session_recovery'
  // Errors
  | 'donation_error'
  | 'donation_payment_failed'
  | 'donation_redirect_failed';

interface DonationEventProperties {
  donation_modal_opened: {
    scope: string;
    recipient_name: string;
    initial_amount: number | null;
    is_redirect_return: boolean;
  };
  donation_amount_selected: { amount: number; is_custom: boolean; currency: string };
  donation_details_submitted: {
    amount: number;
    currency: string;
    is_anonymous: boolean;
    donation_type: 'individual' | 'organization';
    gift_aid: boolean;
    has_platform_fee: boolean;
  };
  donation_checkout_initiated: {
    amount: number;
    total_charge: number;
    currency: string;
    /** Absent for the Stripe handoff — the donation is created on the checkout page */
    donation_id?: string;
    has_paypal?: boolean;
    gateway?: 'adyen' | 'stripe';
  };
  donation_payment_completed: {
    result_code: string;
    donation_id?: string;
    payment_method: string;
    event_source?: 'checkout' | 'redirect_finalized' | 'webhook_settled' | 'paypal';
  };
  donation_completed: {
    donation_id?: string;
    amount: number;
    currency: string;
    event_source?: 'checkout' | 'redirect_finalized' | 'webhook_settled' | 'paypal';
  };
  donation_step_viewed: { step: string; amount?: number };
  donation_modal_closed: { step: string; amount?: number; time_in_step_ms?: number };
  donation_validation_shown: { missing_fields: string[]; missing_count: number };
  donation_redirect_resumed: {
    donation_id: string;
    webhook_settled: boolean;
    has_stored_session: boolean;
  };
  donation_session_recovery: { outcome: 'succeeded' | 'failed'; donation_id?: string };
  donation_error: { step: string; error_type: string; error_message: string };
  donation_payment_failed: {
    result_code?: string;
    donation_id?: string;
    payment_method?: string;
  };
  donation_redirect_failed: { donation_id?: string; reason: string };
}

interface DonationContext {
  scope?: string;
  currency?: string;
  device?: 'mobile' | 'desktop';
}

export function trackDonation<E extends DonationEvent>(
  event: E,
  properties: DonationEventProperties[E],
  context?: DonationContext,
): void {
  try {
    capture(event, {
      ...context,
      ...properties,
    });
  } catch {
    // Analytics must never break the donation flow
  }
}
